/**
 * MOLR scroll-reveal
 * ------------------
 * Progressive-disclosure animation: main-content sections fade in and drift
 * up as they enter the viewport (styles in assets/molr.css, section 5).
 *
 * How it works — and why it's safe:
 * - Classes are added by JS *after* load, so no-JS visitors and crawlers
 *   always see fully visible content.
 * - Skipped entirely for prefers-reduced-motion users and inside the theme
 *   editor (design mode re-renders sections, which would re-hide content).
 * - The hero section runs its own CSS on-load animation and opts out via
 *   [data-molr-no-reveal] on its wrapper.
 *
 * To stagger elements *inside* a section, give them data-molr-reveal-item;
 * this script numbers them so CSS can delay each one slightly.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (!('IntersectionObserver' in window)) return;

  const sections = document.querySelectorAll(
    '#MainContent > .shopify-section:not([data-molr-no-reveal]):not(:has([data-molr-no-reveal]))'
  );
  if (!sections.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('molr-revealed');
        observer.unobserve(entry.target);
      }
    },
    // Trigger once ~12% of the section is visible, slightly before it fully
    // enters, so the motion reads as anticipation rather than lag
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  sections.forEach((section) => {
    // Number the staggered children before the section is observed
    section.querySelectorAll('[data-molr-reveal-item]').forEach((item, index) => {
      item.style.setProperty('--molr-reveal-index', index);
    });

    // Sections already in view at load: reveal without motion to avoid
    // animating content the user is currently reading
    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.6 && rect.bottom > 0) {
      section.classList.add('molr-reveal', 'molr-revealed');
      return;
    }

    section.classList.add('molr-reveal');
    observer.observe(section);
  });
})();
